import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { LocalStorageService } from '../services/local-storage.service';

// publicTracerEndpoints are tracer routes that don't go through auth on the
// backend (registered outside the auth-gated route group in main.go). They
// also don't need realm/agency headers, so we pass them straight through.
//
// Why this list exists: Footer fetches /version on app boot, which can
// fire before Keycloak has finished writing agRealm to localStorage. The
// previous interceptor EMPTY-returned every request without a realm,
// which left the version signal null forever ("—" placeholder).
const publicTracerEndpoints = ['/version', '/ping', '/stats'];

function isPublicEndpoint(url: string): boolean {
  try {
    const path = new URL(url, 'http://placeholder').pathname;
    return publicTracerEndpoints.includes(path);
  } catch {
    // Fallback for non-URL strings: cheap suffix match.
    return publicTracerEndpoints.some(p => url.endsWith(p));
  }
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // Public endpoints — no headers needed, pass straight through.
  if (isPublicEndpoint(req.url)) {
    return next(req);
  }

  const localStorageService = inject(LocalStorageService);
  const agency = localStorageService.getItem('agency');
  const agRealm = localStorageService.getItem('agRealm');

  // Attach realm/agency headers when we have them. If we don't, let the
  // request go out anyway — the backend will respond 401 and the
  // page-level error handler can react (typically by re-routing to
  // login). EMPTY-returning here was masking transient boot-time races
  // (Keycloak hadn't finished writing agRealm yet) by silently dropping
  // the response, leaving callers — like Home's getTracerData and
  // Footer's getVersion — stuck on their loading/placeholder states.
  const headers: Record<string, string> = {};
  if (agency) {
    headers['AG-Agency'] = agency;
  }
  if (agRealm) {
    headers['AG-Realm'] = agRealm;
  }

  const clonedReq = Object.keys(headers).length ? req.clone({ setHeaders: headers }) : req;

  return next(clonedReq);
};
